import { DSModal } from '@repo/design-system/index'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useEventTracking } from '../../../../../helpers/gtmHelper'
import { useJobOptions } from '../../../../../modules/apis/student/lesson'
import { APP_SCREEN_NAME } from '../../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_analysis } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { cafJobStatusQueryApi } from '../../../../../modules/react-query/queryApis'
import urls from '../../../../../modules/urls'
import useCafJobAnalysisSidePopup from '../../CAFOverview/popup/useCafJobAnalysisSidePopup'
import CompanyName from './CompanyName'
import ForeignExperience from './ForeignExperience'
import ForeignExperienceYear from './ForeignExperienceYear'
import Job from './Job'
import JobType from './JobType'
import JobYear from './JobYear'

interface JobInputModalProps {
  showPopup: boolean
  setShowPopup: React.Dispatch<React.SetStateAction<boolean>>
}

export const PAGE_TYPE = {
  JOB: 'job',
  YEAR: 'year',
  TYPE: 'type',
  NAME: 'name',
  FOREIGN_EXPERIENCE: 'foreign_experience',
  FOREIGN_EXPERIENCE_YEAR: 'foreign_experience_year',
}

export const JobInputContent = ({
  jobOptions,
  pageNum,
  setPageNum,
  currentCategory,
  setShowPopup,
  jobInfo,
  setJobInfo,
  handleSubmit,
}) => {
  switch (currentCategory) {
    case PAGE_TYPE.JOB:
      return (
        <Job
          options={jobOptions}
          setShowPopup={setShowPopup}
          pageNum={pageNum}
          setPageNum={setPageNum}
          jobInfo={jobInfo}
          setJobInfo={setJobInfo}
        />
      )
    case PAGE_TYPE.TYPE:
      return (
        <JobType
          options={jobOptions}
          pageNum={pageNum}
          setPageNum={setPageNum}
          jobInfo={jobInfo}
          setJobInfo={setJobInfo}
        />
      )
    case PAGE_TYPE.YEAR:
      return (
        <JobYear
          options={jobOptions}
          pageNum={pageNum}
          setPageNum={setPageNum}
          jobInfo={jobInfo}
          setJobInfo={setJobInfo}
        />
      )
    case PAGE_TYPE.NAME:
      return (
        <CompanyName
          options={jobOptions}
          pageNum={pageNum}
          setPageNum={setPageNum}
          jobInfo={jobInfo}
          setJobInfo={setJobInfo}
        />
      )
    case PAGE_TYPE.FOREIGN_EXPERIENCE:
      return (
        <ForeignExperience
          options={jobOptions}
          pageNum={pageNum}
          setPageNum={setPageNum}
          jobInfo={jobInfo}
          setJobInfo={setJobInfo}
        />
      )
    case PAGE_TYPE.FOREIGN_EXPERIENCE_YEAR:
      return (
        <ForeignExperienceYear
          options={jobOptions}
          pageNum={pageNum}
          setPageNum={setPageNum}
          handleSubmit={handleSubmit}
        />
      )
  }
}

const JobInputModal = ({ showPopup, setShowPopup }: JobInputModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [pageNum, setPageNum] = useState(1)
  const [jobInfo, setJobInfo] = useState()
  const { data: jobOptions } = useJobOptions()
  const totalPages = jobOptions?.category?.length

  const router = useAppRouter()
  const isWebview = useIsWebview()
  const getFullUrl = useReturnFullUrl()
  const { sendPageViewEvent } = useEventTracking()

  const { show, close } = useCafJobAnalysisSidePopup()

  const handleSubmit = () => {
    queryClient.invalidateQueries(cafJobStatusQueryApi.getQueryKey())
    setShowPopup(false)
    if (isWebview) {
      router.push(urls.webview.caf.analysis.jobFilter, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.caf.analysis.jobFilter),
        },
      })
    } else {
      show()
    }
  }

  const questions = [
    {
      type: PAGE_TYPE.JOB,
      title: n_ai_analysis.info_popup_q1,
    },
    {
      type: PAGE_TYPE.TYPE,
      title: n_ai_analysis.info_popup_q2,
    },
    {
      type: PAGE_TYPE.YEAR,
      title: n_ai_analysis.info_popup_q3,
    },
    {
      type: PAGE_TYPE.NAME,
      title: n_ai_analysis.info_popup_q4,
    },
    {
      type: PAGE_TYPE.FOREIGN_EXPERIENCE,
      title: n_ai_analysis.info_popup_q5,
    },
    {
      type: PAGE_TYPE.FOREIGN_EXPERIENCE_YEAR,
      title: n_ai_analysis.info_popup_q6,
    },
  ]

  useEffect(() => {
    if (!router) return
    if (!showPopup) return
    sendPageViewEvent({ page_name: 'ai_analysis_job_segment_require_info_poup', page_context: String(pageNum) })
  }, [router, showPopup, pageNum])

  // popup 반환
  if (showPopup) {
    return (
      <DSModal id={'job-input-modal'} isOpen={showPopup} setIsOpen={setShowPopup} size={'xl'} closeOnDim={true}>
        <DSModal.Title>
          <div className="mb-[10px] flex flex-col">
            <div>
              <span className="Title2-18Bd sm:Headline2-24Bd mb-[8px]">
                {t(questions[pageNum - 1].title).replace(/\*/g, '')}
              </span>
              {pageNum !== 4 && <span className="mb-[8px] text-red-400">*</span>}
            </div>
            <div>
              {pageNum === 1 && (
                <div className="Body2-14Md sm:Body1-16Md text-gray-700">{t(n_ai_analysis.banner_detail)}</div>
              )}
            </div>
            <div className="flex h-[24px] flex-row items-center gap-[4px] px-[2px] py-[8px] text-[14px] font-bold">
              <span className="text-black">{pageNum}</span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-300">{totalPages}</span>
            </div>
          </div>
        </DSModal.Title>
        <DSModal.Content>
          {jobOptions && (
            <JobInputContent
              setShowPopup={setShowPopup}
              currentCategory={questions[pageNum - 1].type}
              jobOptions={jobOptions}
              pageNum={pageNum}
              setPageNum={setPageNum}
              jobInfo={jobInfo}
              setJobInfo={setJobInfo}
              handleSubmit={handleSubmit}
            />
          )}
        </DSModal.Content>
      </DSModal>
    )
  } else {
    return null
  }
}

export default React.memo(JobInputModal)
